import React, { useState } from 'react';
import { X, Lock, CheckCircle } from 'lucide-react';
import Button from './Button';
import { supabase } from '../../supabase';
import CustomDialog from './CustomDialog';

import logo from '../../assets/images/pyrus_media_logo.png';

const EnrollmentModal = ({ isOpen, onClose, course }) => {
    const [step, setStep] = useState(1); // 1: Form, 2: Processing, 3: Success
    const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', type: 'success' });

    const [formErrors, setFormErrors] = useState({});

    if (!isOpen || !course) return null;

    const validateForm = () => {
        const errors = {};
        // Name Validation
        if (!formData.name.trim()) errors.name = 'Name is required';

        // Email Validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email || !emailRegex.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        // Phone Validation (Indian Format: 10 digits, starts with 6-9)
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!formData.phone || !phoneRegex.test(formData.phone)) {
            errors.phone = 'Please enter a valid 10-digit Indian mobile number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async (e) => {
        e.preventDefault();

        // Validate Form before proceeding
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            // 1. Load Razorpay SDK
            const res = await loadRazorpay();
            if (!res) {
                throw new Error('Razorpay SDK failed to load. Are you online?');
            }

            // 2. Create Order via Edge Function
            // Note: We're invoking the function we just created.
            // Ensure you have deployed it using `supabase functions deploy create-razorpay-order`
            const { data: orderData, error: orderError } = await supabase.functions.invoke('create-razorpay-order', {
                body: {
                    courseId: course.id,
                    amount: parseFloat(course.price.replace(/[^0-9.]/g, '')) // Extract number from "$49" or "499 INR"
                }
            });

            if (orderError) throw orderError;
            if (!orderData) throw new Error('No order data received');


            // 3. Initialize Razorpay Payment
            // RAZORPAY CUSTOMIZATION OPTIONS
            // You can customize the look and feel of the payment popup here.
            const options = {
                key: orderData.key_id, // Enter the Key ID generated from the Dashboard
                amount: orderData.amount,
                currency: orderData.currency,

                // APP DETAILS
                name: "PyrusMedia", // Title of the popup
                description: `Enrollment: ${course.title}`, // Subtitle
                image: logo, // Your Logo (ensure path is accessible)
                order_id: orderData.order_id,
                handler: async function (response) {
                    // 4. Payment Success - Verify & Record via Edge Function
                    try {
                        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-razorpay-payment', {
                            body: {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                course_id: course.id,
                                student_details: {
                                    name: formData.name,
                                    email: formData.email,
                                    phone: formData.phone,
                                    amount: orderData.amount / 100
                                }
                            }
                        });

                        if (verifyError || !verifyData.success) throw new Error(verifyError?.message || 'Payment verification failed');

                        setStep(3); // Show Success
                    } catch (err) {
                        console.error('Verification Error:', err);
                        setError('Payment successful but verification failed. Please contact support with Order ID: ' + response.razorpay_order_id);
                    }
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                        // Optional: Show message on cancellation
                    }
                },
                prefill: {
                    name: formData.name,
                    email: formData.email,
                    contact: `+91${formData.phone}` // Auto-fill phone with code
                },
                theme: {
                    // CUSTOMIZE COLOR HERE (Main Brand Color)
                    color: "#bebd19"
                }
            };

            const paymentObject = new window.Razorpay(options);

            // Handle Payment Failure
            paymentObject.on('payment.failed', function (response) {
                console.error('Payment Failed:', response.error);
                setError(`Payment Failed: ${response.error.description || 'Unknown Error'}`);
                setLoading(false);
            });

            paymentObject.open();

        } catch (err) {
            console.error('Payment Error:', err);
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            // setLoading(false); // Removed because we want to keep loading until modal opens or fails
        }
    };

    const handleClose = () => {
        setStep(1);
        setFormData({ name: '', email: '', phone: '' });
        setFormErrors({});
        setError('');
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-lg relative animate-in fade-in zoom-in duration-200">
                <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-white">
                    <X size={20} />
                </button>

                <div className="p-8">
                    {step === 1 && (
                        <>
                            <h2 className="text-2xl font-bold text-white mb-2">Enroll in {course.title}</h2>
                            <p className="text-gray-400 mb-6">Enter your details to proceed to payment.</p>

                            {error && <div className="bg-red-500/10 text-red-500 p-3 rounded mb-4 text-sm">{error}</div>}

                            <form onSubmit={handlePayment} className="space-y-4">
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => {
                                            setFormData({ ...formData, name: e.target.value });
                                            if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                                        }}
                                        className={`w-full bg-black border ${formErrors.name ? 'border-red-500' : 'border-gray-800'} rounded p-3 text-white focus:border-[#bebd19] outline-none`}
                                    />
                                    {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Email Address</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => {
                                            setFormData({ ...formData, email: e.target.value });
                                            if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                                        }}
                                        className={`w-full bg-black border ${formErrors.email ? 'border-red-500' : 'border-gray-800'} rounded p-3 text-white focus:border-[#bebd19] outline-none`}
                                    />
                                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Phone Number</label>
                                    <div className={`flex items-center bg-black border ${formErrors.phone ? 'border-red-500' : 'border-gray-800'} rounded overflow-hidden`}>
                                        <div className="bg-gray-900 px-3 py-3 text-gray-400 border-r border-gray-800 select-none">
                                            +91
                                        </div>
                                        <input
                                            type="tel"
                                            maxLength="10"
                                            value={formData.phone}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, ''); // Only numbers
                                                setFormData({ ...formData, phone: val });
                                                if (formErrors.phone) setFormErrors({ ...formErrors, phone: '' });
                                            }}
                                            className="w-full bg-black p-3 text-white focus:outline-none"
                                            placeholder="9876543210"
                                        />
                                    </div>
                                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                                </div>

                                <div className="pt-4">
                                    <div className="flex justify-between items-center mb-4 p-4 bg-gray-900 rounded-lg">
                                        <span className="text-gray-300">Total Amount</span>
                                        <span className="text-xl font-bold text-[#bebd19]"><span>&#8377;</span> {course.price.replace(/[^0-9.]/g, '')}</span>
                                    </div>

                                    <Button type="submit" disabled={loading} className="w-full justify-center py-4 text-base">
                                        {loading ? 'Processing...' : 'Proceed to Payment'} <Lock size={16} className="ml-2" />
                                    </Button>
                                    <p className="text-center text-xs text-gray-500 mt-3 flex items-center justify-center gap-1">
                                        <Lock size={12} /> Secure payment via Razorpay
                                    </p>
                                </div>
                            </form>
                        </>
                    )}

                    {step === 3 && (
                        <div className="text-center py-10">
                            <div className="w-20 h-20 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle size={40} />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Enrollment Successful!</h2>
                            <p className="text-gray-400 mb-8">Thank you, {formData.name}. We have sent a confirmation email to {formData.email}.</p>
                            <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700 w-full justify-center">
                                Done
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <CustomDialog
                isOpen={dialog.isOpen}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                onClose={() => setDialog({ ...dialog, isOpen: false })}
            />
        </div>
    );
};

export default EnrollmentModal;
